// react and mui components/utils
import { Dispatch, SetStateAction, useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import Avatar from "@mui/joy/Avatar"
import Skeleton from "@mui/joy/Skeleton"
import Tab from "@mui/joy/Tab"
import TabList from "@mui/joy/TabList"
import TabPanel from "@mui/joy/TabPanel"
import Tabs from "@mui/joy/Tabs"
import Tooltip from "@mui/joy/Tooltip"
import Typography from "@mui/joy/Typography"
import Stack from "@mui/joy/Stack";

import KeyboardVoice from "@mui/icons-material/KeyboardVoice";
import Textsms from "@mui/icons-material/Textsms";

import { ToastContext } from "../components/toast";
import { UserContext } from "../utils/contexts";

// custom components
import Badges from "../components/profileTabs/badges";
import IGNs from "../components/profileTabs/igns";
import RoleForm from "../components/profileTabs/roles";
import ModHistory from "../components/profileTabs/mod";
import Bank from "../components/profileTabs/bank";

// data and stuff
import { BaseUserProfile, CliUser } from "../types/users";
import { profileTabs } from "../data/placeholderusers";
import { setData } from "../utils/setdata";
import { updateUser } from "../utils/utils";
import { imgSrc, userAPI } from "../utils/backend";

const panelSX = { height: "10%", overflowY: "auto", overflowX: "auto", width: "100%" };

const listSX = { flex: 'none', scrollSnapAlign: 'start' };

export default function ProfileRouter() {
  const params = useParams()
  const { user, setUser } = useContext(UserContext);
  
  if (!user) return <></>
  const id = params.id || user.id
  
  return <Profile id={id} setUser={setUser} user={user}/>
}

type ProfileProps = {
  id: string;
  user: CliUser;
  setUser: Dispatch<SetStateAction<CliUser | null | undefined>>;
}

function Profile({ id, user, setUser }: ProfileProps) {
  const hash = window.location.hash.substring(1);
  const [panel, setPanel] = useState(profileTabs(user, id).includes(hash) ? hash : "badges");
  const [profile, setProfile] = useState<BaseUserProfile | CliUser>();

  const { setToast } = useContext(ToastContext);
  
  const nav = useNavigate();

  useEffect(() => {
    const h = window.location.hash.substring(1)
    setPanel(profileTabs(user, profile?.id ?? user.id).includes(h) ? h : "badges")
  }, [profile, user])

  useEffect(() => {
    if (!user) return;
    if (profile && id === profile.id) return;

    setProfile(undefined)

    if (id === user.id && user.badges) {
      return setProfile(user);
    }

    // but if not, fetch the user and set the tab (only setting here so it doesn't reset every time you submit a form)
    userAPI.get(id).then(usr => {
      if (!setData(usr, setToast, user, setUser, () => nav("/profile"))) return;

      if (usr.user === null) {
        setToast({ text: "Sorry, I couldn't find that person.", color: "warning" });
        return nav("/profile");
      }

      if (id === user.id) setUser(updateUser(user, usr.user));
      else setProfile(usr.user);
    })
  }, [user, setUser, setToast, nav, id, profile]);

  if (!user) return <div/>

  const rank = profile?.badges?.find(b => b.image.startsWith("chat"))

  return (
    <div>
      <header>
        <h1 className="pageTitle" style={{ display: "flex", flexDirection: "column" }}>
          <Stack direction={{ xs: "column", sm: "row" }} sx={{ justifyContent: "center", alignItems: "center" }}>
            <Avatar src={profile?.avatar  + "?size=64"} alt={profile?.displayName} style={{ marginRight: "10px", width: "64px", height: "64px" }}>
              <Skeleton loading={!profile}/>
            </Avatar>
            <Typography level="h1">
              <Skeleton loading={!profile}>
                {profile?.displayName ?? "Robertboy"}'s Profile
              </Skeleton>
            </Typography>
          </Stack>
          {profile?.activePosts !== undefined &&
            <div className="flexMidHoriz flexMidVert" style={{ marginBottom: "10px" }}>
              <Typography startDecorator={<Textsms/>} sx={{ marginRight: "20px" }}>{profile.activePosts}</Typography>
              <Typography startDecorator={<KeyboardVoice/>}>{profile.activeVoice}</Typography>
            </div>
          }
          <div className="flexMidHoriz flexMidVert">
            {rank &&
              <Tooltip title={rank.name + " Rank"}>
                <img src={imgSrc("badges/" + rank.image)} height="24px" width="24px" alt="" style={{ marginRight: "5px" }}/>
              </Tooltip>
            }
            <Typography level="body-lg">
              <Skeleton loading={!profile}>
                Level {profile?.level ?? 10} | Lifetime XP: {profile?.xp?.lifetime ?? 10000}
              </Skeleton>
            </Typography>
          </div>
        </h1>
      </header>

      <div className="contentRoot">
        <Tabs size="lg" sx={{ height: "100%" }} value={panel} onChange={(e, v) => {
          window.history.replaceState(null, "", "#" + v?.toString() ?? "badges")
          setPanel(v?.toString() ?? "badges")
        }}>
          <TabList sx={{
            overflow: 'auto',
            scrollSnapType: 'x mandatory',
            '&::-webkit-scrollbar': { display: 'none' },
          }}>
            <Tab value={"badges"} sx={listSX}>Badges</Tab>
            <Tab value={"igns"} sx={listSX}>IGNs</Tab>
            {id === user.id && <>
              <Tab value={"roles"} sx={listSX}>Roles</Tab>
              <Tab value={"transactions"} sx={listSX}>🔒 Transactions</Tab>
            </>}
            {(id === user.id || user.perms.isMod) && <Tab value={"infractions"} sx={listSX}>🔒 Mod History</Tab>}
          </TabList>
          {profile && <>
            <TabPanel sx={panelSX} value={"badges"}><Badges currentUser={profile}/></TabPanel>
            <TabPanel sx={panelSX} value={"igns"}><IGNs currentUser={profile}/></TabPanel>
            <TabPanel sx={panelSX} value={"roles"}><RoleForm/></TabPanel>
            <TabPanel sx={panelSX} value={"transactions"}><Bank setProfile={setProfile}/></TabPanel>
            <TabPanel sx={panelSX} value={"infractions"}><ModHistory profile={profile} setProfile={setProfile}/></TabPanel>
          </>}
        </Tabs>
      </div>
    </div>
  )
} 